import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Destinations() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/destinations`).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const grouped = items.reduce((acc, d) => {
    acc[d.country] = acc[d.country] || [];
    acc[d.country].push(d);
    return acc;
  }, {});

  return (
    <section id="destinations" className="relative bg-[#1A1A1A] text-[#F9F6F0] py-24 md:py-40 z-[2]" data-testid="destinations-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#C5A059]" />
              <span className="eyebrow text-[#C5A059]">Eight Stays · Two Countries</span>
            </div>
            <h2 className="font-serif-editorial text-5xl md:text-7xl leading-[0.95] tracking-tight">
              Where you&rsquo;ll <span className="italic text-[#E6C5C0]">unfold</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 flex md:items-end">
            <p className="text-[#F9F6F0]/70 leading-relaxed">
              Every property is Catalonia-affiliated. Adults-only sanctuaries, family-forward beachfronts, and one hidden adults escape in the jungle. Your concierge tailors your preferred accommodations once you arrive.
            </p>
          </div>
        </div>

        <div className="space-y-24 md:space-y-28">
          {Object.entries(grouped).map(([country, list], countryIdx) => (
            <div key={country} data-testid={`destinations-country-${country.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-baseline justify-between mb-8 md:mb-12 border-b border-[#F9F6F0]/15 pb-4">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif-editorial text-[#C5A059] text-2xl">{String(countryIdx + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif-editorial text-3xl md:text-5xl italic">{country}</h3>
                </div>
                <span className="eyebrow text-[#F9F6F0]/60">{list.length} Resorts</span>
              </div>

              <div className="grid grid-cols-12 gap-6 md:gap-8">
                {list.map((d, i) => (
                  <motion.article
                    key={d.id}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, delay: i * 0.06, ease: [0.19, 1, 0.22, 1] }}
                    className={`group col-span-12 sm:col-span-6 ${i % 3 === 0 ? "lg:col-span-6" : "lg:col-span-3"}`}
                    data-testid={`destination-${d.id}`}
                  >
                    <div className={`relative overflow-hidden ${i % 3 === 0 ? "aspect-[16/10]" : "aspect-[3/4]"} bg-[#262624] mb-5`}>
                      <img src={d.image} alt={d.name}
                           className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105" />
                      {d.adults_only && (
                        <span className="absolute top-4 left-4 eyebrow bg-[#F9F6F0] text-[#1A1A1A] px-3 py-1.5 rounded-full">Adults Only</span>
                      )}
                      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="eyebrow text-[#C5A059] mb-2 flex items-center gap-2">
                          <MapPin size={11} strokeWidth={1.5} /> {d.region}
                        </p>
                        <h4 className="font-serif-editorial text-xl md:text-2xl leading-tight">{d.name}</h4>
                        <p className="text-sm text-[#F9F6F0]/60 mt-2 leading-relaxed max-w-md">{d.description}</p>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
